/*
 * Governance Tile · Phase 3 · "How E1 is going" column.
 *
 * Renders the E1 column of the three-column live scorecard.  Every
 * measured metric binds to the live response from
 *   GET /api/admin/governance-tile/summary
 * exposed via useTileData().  Every metric the endpoint does not yet
 * compute renders as an em dash with a "not yet measured" tooltip and
 * a `-pending` testid, so no mockup placeholder integer ever ships
 * (§ V.6 attestation-honesty rule).
 *
 * Ritual sub-check state (T-SS · T-SE · C1-C6 · Q1-Q5 · MGD 1-6 ·
 * FORK 1-6) is not yet measured by the backend either.  The renderer
 * substitutes the mockup's cosmetic glyphs (✓ / ✗) with the
 * "unknown" glyph (◯) and clamps every group's chip to
 * "Not measured yet" until IJ-079 wires the per-check scan.
 *
 * IJ-083 · 2026-07-24 · Phase 3 of the v4-to-v8.8.12 re-cut.
 */
import { useState } from 'react';
import Row from './Row';
import {
  E1_SCORECARD_TITLE,
  E1_SCORECARD_ICON,
  E1_SUBTITLE,
  E1_RITUALS_PARENT_LABEL,
  E1_RITUALS_PARENT_TOOLTIP,
  E1_RITUALS,
  E1_WORK_LANDED_LABEL,
  E1_WORK_LANDED,
  E1_CONFIDENCE_LABEL,
  E1_CONFIDENCE,
  E1_WORDS_LABEL,
  E1_WORDS,
  E1_LOOSE_ENDS_LABEL,
  E1_LOOSE_ENDS,
} from '../copy/e1Column';

const PENDING = '\u2014'; // em dash
const RITUAL_PENDING_CHIP = 'Not measured yet';
const PENDING_TOOLTIP =
  'Not measured yet. Live counter pending IJ-079 (backend counter service).';

// Row-label -> extractor(summary) OR null (pending IJ-079).
// Extractors return { value, tone } or null when the summary is not
// yet loaded.  A null result means "measured but backend still fetching",
// distinct from PENDING which means "field does not exist yet".
const WORK_LANDED_MAP = {
  'Done right, first time': (s) => ({
    value: String(s.right_first_time ?? 0),
    tone: (s.right_first_time ?? 0) > 0 ? 'green' : 'slate',
  }),
  'Wrong (operator caught before or after ship)': (s) => ({
    value: String(s.got_wrong ?? 0),
    tone: (s.got_wrong ?? 0) > 0 ? 'red' : 'slate',
  }),
  'Corrected before ship (Judge caught me)': (s) => ({
    value: String(s.corrected_on_fly ?? 0),
    tone: (s.corrected_on_fly ?? 0) > 0 ? 'amber' : 'slate',
  }),
  'Paused to ask you for clarification': (s) => ({
    value: String(s.paused_to_ask ?? 0),
    tone: 'slate',
  }),
  'Skipped (asked, not done)': (s) => ({
    value: String(s.things_skipped ?? 0),
    tone: (s.things_skipped ?? 0) > 0 ? 'red' : 'slate',
  }),
  // Pending IJ-079 · left null so the renderer falls through to em dash
  'Actioned without your green light': null,
  // Pending IJ-079 · attestation-honesty scan not yet wired
  'Said I did but really didn\u2019t (attestation-honesty)': null,
  'QA / eyeball not executed': (s) => ({
    value: String(s.qa_not_done ?? 0),
    tone: (s.qa_not_done ?? 0) > 0 ? 'red' : 'slate',
  }),
  // Section total — honest: em dash while any component is pending
  'Section total (wrong + skipped + no-approval + attestation + QA)': null,
};

const CONFIDENCE_MAP = {
  // "E1 referred to past lessons" is not yet emitted separately from
  // the Judge counter.  Pending IJ-079.
  'E1 referred to past lessons': null,
  'When E1 said HIGH confidence, was right': (s) => ({
    value: `${s.confidence_high_right ?? 0} of ${s.confidence_high_total ?? 0}`,
    tone: (s.confidence_high_total ?? 0) > 0
      && (s.confidence_high_right ?? 0) < (s.confidence_high_total ?? 0)
      ? 'red' : 'slate',
  }),
  'New lessons E1 codified today': (s) => ({
    value: String(s.new_lessons_learned ?? 0),
    tone: (s.new_lessons_learned ?? 0) > 0 ? 'amber' : 'slate',
  }),
  // SPD v6.01 · IJ-092 — bind to live avg_turn_seconds from ledger.
  // When the endpoint returns null (fewer than 2 turns · not enough
  // data to average) we still emit a valid payload so the badge
  // renders em dash rather than collapsing off-screen.  Returning
  // null here would trip resolveRow's spread-of-null path.
  'Average E1 turn time': (s) => {
    if (s.avg_turn_seconds === null || s.avg_turn_seconds === undefined) {
      return { value: PENDING, tone: 'slate' };
    }
    return { value: `${s.avg_turn_seconds}s`, tone: 'slate' };
  },
};

const WORDS_MAP = {
  'New copy strings I introduced': (s) => ({
    value: String(s.words_new_this_session ?? 0),
    tone: 'slate',
  }),
  'You approved': (s) => ({
    value: String(s.words_approved ?? 0),
    tone: (s.words_approved ?? 0) > 0 ? 'green' : 'slate',
  }),
  // Revocation / backlog scan not yet wired.  Pending IJ-079.
  'You revoked or backlogged': null,
  'Waiting for your approval': (s) => ({
    value: String(s.words_waiting_approval ?? 0),
    tone: (s.words_waiting_approval ?? 0) > 0 ? 'amber' : 'slate',
  }),
  'Section total (new copy strings this session)': (s) => ({
    value: String(s.words_new_this_session ?? 0),
    tone: 'slate',
  }),
};

const LOOSE_ENDS_MAP = {
  'Rule-breaks still open': (s) => ({
    value: String(s.loose_ends_open ?? 0),
    tone: (s.loose_ends_open ?? 0) > 0 ? 'red' : 'slate',
  }),
  // SPD v6.01 · IJ-092 — bind to today's aggregated sev breach count.
  'Rule-breaks that fired this session': (s) => {
    const buckets = s.sev_counters;
    if (!buckets) return null;
    const today =
      (buckets.sev_0?.today ?? 0) +
      (buckets.sev_1?.today ?? 0) +
      (buckets.sev_2?.today ?? 0) +
      (buckets.sev_3?.today ?? 0);
    return { value: String(today), tone: today > 0 ? 'red' : 'slate' };
  },
  // SPD v6.01 · IJ-092 — live files-over-500 scanner result.
  'Files over 500-line ceiling': (s) => {
    if (s.files_over_500 === null || s.files_over_500 === undefined) return null;
    return {
      value: String(s.files_over_500),
      tone: s.files_over_500 > 0 ? 'red' : 'slate',
    };
  },
};

function resolveRow(row, map, summary) {
  const extractor = map[row.label];
  if (extractor === null || extractor === undefined) {
    // Field does not exist in the endpoint yet.
    return {
      value: PENDING,
      tone: 'slate',
      pending: true,
      tooltip: row.tooltip
        ? `${row.tooltip} · ${PENDING_TOOLTIP}`
        : PENDING_TOOLTIP,
    };
  }
  if (!summary) {
    // Summary still loading.
    return { value: PENDING, tone: 'slate', pending: false, tooltip: row.tooltip };
  }
  const resolved = extractor(summary);
  return { ...resolved, pending: false, tooltip: row.tooltip };
}

function SectionHeader({ label }) {
  return (
    <h4 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mt-4 mb-2">
      {label}
    </h4>
  );
}

// SPD v6.01 · IJ-092 — sub-check key mappings for the CBP and QABP
// ritual groups.  Each entry maps a sub-check index (0-based) to the
// endpoint field in `summary.judge_sub_check_counts` that reports
// whether the Judge touched that check today.
const LIVE_SUB_CHECK_KEYS = {
  cbp: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'],
  qabp: ['q1', 'q2', 'q3', 'q4', 'q5'],
};

function subCheckState(groupKey, i, summary) {
  const keys = LIVE_SUB_CHECK_KEYS[groupKey];
  if (!keys || !summary?.judge_sub_check_counts) return { measured: false };
  const endpointKey = keys[i];
  if (!endpointKey) return { measured: false };
  const hits = summary.judge_sub_check_counts[endpointKey] ?? 0;
  return { measured: true, hits };
}

function ritualChip(group, summary) {
  const keys = LIVE_SUB_CHECK_KEYS[group.key];
  if (!keys || !summary?.judge_sub_check_counts) return RITUAL_PENDING_CHIP;
  const passed = keys.filter((k) => (summary.judge_sub_check_counts[k] ?? 0) > 0).length;
  return `${passed}/${keys.length} clean`;
}

function RitualGroup({ group, summary }) {
  const [open, setOpen] = useState(false);
  const chipText = ritualChip(group, summary);
  const isMeasured = chipText !== RITUAL_PENDING_CHIP;
  return (
    <div
      data-testid={`governance-tile-e1-ritual-${group.key}`}
      className={
        'border-l-4 pl-3 py-1 rounded-r bg-slate-900/40 ' + group.borderColour
      }
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full grid grid-cols-[1fr_auto] gap-x-3 items-center text-left"
      >
        <span className="text-[12px] text-slate-200 leading-snug">
          <span
            title={group.tooltip}
            className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 text-[9px] text-slate-400 cursor-help align-middle"
          >
            ?
          </span>
          {group.parent}
        </span>
        <span
          data-testid={`governance-tile-e1-ritual-${group.key}-chip`}
          className={
            'text-[10px] font-medium tabular-nums whitespace-nowrap px-2 py-0.5 rounded border ' +
            (isMeasured
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
              : 'border-slate-500/30 bg-slate-500/10 text-slate-300')
          }
        >
          {chipText}
        </span>
      </button>
      {open ? (
        <ul className="mt-2 space-y-0.5 pl-1">
          {group.subChecks.map((sc, i) => {
            const state = subCheckState(group.key, i, summary);
            let glyph = '\u25EF'; // ◯
            let glyphColour = 'text-slate-500';
            let testIdSuffix = '-pending';
            if (state.measured) {
              testIdSuffix = '';
              if (state.hits > 0) {
                glyph = '\u2713'; // ✓
                glyphColour = 'text-emerald-400';
              } else {
                glyph = '\u2717'; // ✗
                glyphColour = 'text-rose-400';
              }
            }
            return (
              <li
                key={i}
                data-testid={`governance-tile-e1-ritual-${group.key}-sub-${i}${testIdSuffix}`}
                className="text-[11px] text-slate-400 leading-snug"
              >
                <span className={`mr-1.5 ${glyphColour}`}>{glyph}</span>
                {sc.label}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function MetricSection({ title, rows, map, summary, testIdPrefix }) {
  return (
    <div>
      <SectionHeader label={title} />
      <div className="space-y-0">
        {rows.map((row, i) => {
          const r = resolveRow(row, map, summary);
          return (
            <Row
              key={i}
              label={row.label}
              tooltip={r.tooltip}
              value={r.value}
              tone={r.tone}
              testId={
                r.pending
                  ? `${testIdPrefix}-${i}-pending`
                  : `${testIdPrefix}-${i}`
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export default function E1Column({ summary, loading, error }) {
  const workingSummary = error ? null : summary;
  const rightFirst = workingSummary?.right_first_time ?? null;
  const tasksTotal = workingSummary?.tasks_total ?? null;
  const gotWrong = workingSummary?.got_wrong ?? null;

  const headlineValue = rightFirst === null ? PENDING : String(rightFirst);
  const headlineSuffix =
    tasksTotal === null ? ` ${PENDING}` : ` of ${tasksTotal}`;
  const headlineSubRed =
    gotWrong === null || tasksTotal === null
      ? `${PENDING} caught wrong by you`
      : tasksTotal === 0
        ? 'No turns logged in this session yet.'
        : `${gotWrong} of ${tasksTotal} caught wrong by you`;

  return (
    <section
      data-testid="governance-tile-e1-column"
      className="rounded-xl border border-slate-800/70 bg-slate-950/40 p-5"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[16px] text-slate-300" aria-hidden="true">
          {E1_SCORECARD_ICON}
        </span>
        <h3 className="text-[15px] font-semibold text-slate-100">
          {E1_SCORECARD_TITLE}
        </h3>
      </div>
      <p className="text-[11px] text-slate-400 leading-snug mb-4">
        {E1_SUBTITLE}
      </p>

      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span
            data-testid="governance-tile-e1-headline-value"
            className="text-[36px] font-bold text-slate-100 tabular-nums leading-none"
          >
            {headlineValue}
          </span>
          <span className="text-[13px] text-slate-400">{headlineSuffix}</span>
        </div>
        <p
          data-testid="governance-tile-e1-headline-sub"
          className="text-[11px] text-rose-300 leading-snug mt-1"
        >
          {headlineSubRed}
        </p>
      </div>

      <div>
        <div className="flex items-center gap-1 mb-1">
          <span
            title={E1_RITUALS_PARENT_TOOLTIP}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 text-[9px] text-slate-400 cursor-help"
          >
            ?
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
            {E1_RITUALS_PARENT_LABEL}
          </span>
        </div>
        <div className="space-y-1.5 mb-4">
          {E1_RITUALS.map((g) => (
            <RitualGroup key={g.key} group={g} summary={workingSummary} />
          ))}
        </div>
      </div>

      <MetricSection
        title={E1_WORK_LANDED_LABEL}
        rows={E1_WORK_LANDED}
        map={WORK_LANDED_MAP}
        summary={workingSummary}
        testIdPrefix="governance-tile-e1-work"
      />
      <MetricSection
        title={E1_CONFIDENCE_LABEL}
        rows={E1_CONFIDENCE}
        map={CONFIDENCE_MAP}
        summary={workingSummary}
        testIdPrefix="governance-tile-e1-confidence"
      />
      <MetricSection
        title={E1_WORDS_LABEL}
        rows={E1_WORDS}
        map={WORDS_MAP}
        summary={workingSummary}
        testIdPrefix="governance-tile-e1-words"
      />
      <MetricSection
        title={E1_LOOSE_ENDS_LABEL}
        rows={E1_LOOSE_ENDS}
        map={LOOSE_ENDS_MAP}
        summary={workingSummary}
        testIdPrefix="governance-tile-e1-loose"
      />

      {loading ? (
        <p
          data-testid="governance-tile-e1-loading"
          className="mt-3 text-[10px] italic text-slate-500"
        >
          Live counters loading…
        </p>
      ) : null}
      {error ? (
        <p
          data-testid="governance-tile-e1-error"
          className="mt-3 text-[10px] text-rose-400"
        >
          Live counter fetch failed. Rows show pending until the endpoint recovers.
        </p>
      ) : null}
    </section>
  );
}
